import Tooltip from "@components/Atoms/Tooltip";
import type { CommentBlock } from "@core-ui/CommentBlock";
import { useModalBlocker } from "@core-ui/stores/ModalBlockerStore";
import { ARTICLE_POPOVER_PADDING, getArticlePopoverBoundary } from "@ext/markdown/core/edit/logic/articlePopover";
import { Comment } from "@ext/markdown/elements/comment/edit/components/Popover/Comment";
import commentPlacement from "@ext/markdown/elements/comment/edit/logic/commentPlacement";
import { confirmCommentClose } from "@ext/markdown/elements/comment/edit/logic/confirmCommentClose";
import GlobalEditorIsEditable from "@ext/markdown/elements/comment/edit/logic/GlobalIsEditable";
import getCommentIdAtPosition from "@ext/markdown/elements/comment/edit/logic/getCommentIdAtPosition";
import useCurrentAuthor from "@ext/markdown/elements/comment/edit/logic/hooks/useCurrentAuthor";
import { markItemAsRead } from "@ext/review/logic/store/ReviewNotificationsStore";
import { type Editor, type JSONContent, posToDOMRect, type Range } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import { isInDropdown } from "@ui-kit/Dropdown";
import { type CSSProperties, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Instance, Props } from "tippy.js";
import { isCommentBlockDirty } from "../../logic/isCommentBlockDirty";

export type CommentViewProps = {
	commentId: string;
	editor: Editor;
	loadComment: (id: string) => Promise<CommentBlock>;
	saveComment: (id: string, comment: CommentBlock) => void;
	deleteComment: (id: string, positions: Range[]) => void;
};

const isBlockAt = (doc: Node, pos: number) => pos < doc.content.size && Boolean(doc.nodeAt(pos)?.isBlock);

const CommentView = memo((props: CommentViewProps) => {
	const { editor, commentId, loadComment, saveComment, deleteComment } = props;
	const isReadOnly = !editor.isEditable;
	const [data, setData] = useState<CommentBlock>(null);
	const [dataCommentId, setDataCommentId] = useState<string>(null);
	const [isOpen, setIsOpen] = useState(false);
	const appendCommentToBody =
		editor.extensionManager.extensions.find((ext) => ext.name === "comment")?.options.appendCommentToBody ?? false;

	const elementRef = useRef<HTMLDivElement>(null);
	const openedCommentIdRef = useRef<string>(null);
	const closingCommentIdRef = useRef<string>(null);
	const instanceRef = useRef<Instance<Props>>(null);
	const flagNoDeleteRef = useRef<boolean>(null);
	const isOpenRef = useRef(false);

	const author = useCurrentAuthor();

	const close = useCallback(() => {
		isOpenRef.current = false;
		closingCommentIdRef.current = openedCommentIdRef.current;
		setIsOpen(false);
	}, []);

	/** a closing popover asks nothing: whatever it still shows was already sent or dismissed */
	const hideComment = useCallback(() => {
		if (!isOpenRef.current) return;
		if (isReadOnly || !isCommentBlockDirty(instanceRef.current)) return close();
		void confirmCommentClose().then((result) => {
			if (result) close();
		});
	}, [isReadOnly, close]);

	useEffect(() => {
		if (!commentId) return;
		openedCommentIdRef.current = commentId;

		let cancelled = false;
		void (async () => {
			const comment = (await loadComment(commentId)) || {};
			if (cancelled) return;
			setData(comment);
			setDataCommentId(commentId);
			isOpenRef.current = true;
			setIsOpen(true);
			markItemAsRead(commentId);
		})();

		return () => {
			cancelled = true;
		};
	}, [commentId, loadComment]);

	useEffect(() => {
		if (!editor || editor.isDestroyed) return;

		const onSelectionUpdate = ({ editor }: { editor: Editor }) => {
			const openedCommentId = openedCommentIdRef.current;
			if (!openedCommentId) return;

			const id = getCommentIdAtPosition(editor.state, editor.state.selection.anchor);
			if (!id) return hideComment();

			if (openedCommentId === id) return;
			hideComment();
		};

		const onKeyDown = (event: KeyboardEvent) => {
			const instance = instanceRef.current;
			if (event.key === "Escape" && instance?.state.isVisible) hideComment();
		};

		document.addEventListener("keydown", onKeyDown, { capture: true });
		editor.on("selectionUpdate", onSelectionUpdate);

		return () => {
			document.removeEventListener("keydown", onKeyDown, { capture: true });
			editor.off("selectionUpdate", onSelectionUpdate);
		};
	}, [editor, hideComment]);

	useModalBlocker("comment-popover", isOpen);

	const onHidden = useCallback(() => {
		if (isOpen || openedCommentIdRef.current !== closingCommentIdRef.current) return;

		if (!data?.comment && !flagNoDeleteRef.current) editor.commands.unsetCurrentComment();
		else editor.commands.closeComment();

		flagNoDeleteRef.current = null;

		setData(null);
		setDataCommentId(null);
		openedCommentIdRef.current = null;
	}, [editor, data, isOpen]);

	const createComment = useCallback(
		(content: JSONContent[]) => {
			const newData = {
				comment: {
					dateTime: new Date().toISOString(),
					user: author,
					content,
				},
				answers: [],
			};
			saveComment(openedCommentIdRef.current, newData);
			close();
		},
		[saveComment, close, author],
	);

	const onAddAnswer = useCallback(
		(commentBlock: CommentBlock, hide: boolean = true) => {
			saveComment(openedCommentIdRef.current, commentBlock);
			flagNoDeleteRef.current = true;

			const instance = instanceRef.current;
			if (hide) return close();

			requestAnimationFrame(() => {
				if (instance?.popperInstance) {
					instance.popperInstance.update();
				}
			});
			setData(commentBlock);
		},
		[saveComment, close],
	);

	const onDelete = useCallback(() => {
		if (!editor || editor.isDestroyed) return;

		const commentId = openedCommentIdRef.current;
		if (!commentId) return;

		const positions = editor.storage.comment.positions.get(commentId) || [];
		const position = editor.storage.comment.openedComment?.position ?? positions.at(-1);

		deleteComment(commentId, positions);
		close();
		if (position && isBlockAt(editor.state.doc, position.from))
			editor.chain().setNodeSelection(position.from).focus(null, { scrollIntoView: false }).run();
		else editor.commands.focus(position?.to, { scrollIntoView: false });
	}, [deleteComment, editor, close]);

	const onDeleteAnswer = useCallback(
		(commentBlock: CommentBlock) => {
			saveComment(openedCommentIdRef.current, commentBlock);

			requestAnimationFrame(() => {
				if (instanceRef.current?.popperInstance) {
					instanceRef.current.popperInstance.update();
				}
			});
			setData(commentBlock);
		},
		[saveComment],
	);

	const onCreate = useCallback(
		(commentBlock: CommentBlock) => {
			flagNoDeleteRef.current = true;
			createComment(commentBlock.comment.content);
		},
		[createComment],
	);

	const styles = useMemo(() => {
		return {
			visibility: "hidden",
		} as CSSProperties;
	}, []);
	const popoverBoundary = appendCommentToBody ? "viewport" : getArticlePopoverBoundary(editor, undefined, "viewport");
	const popoverPadding = appendCommentToBody ? { left: 8, right: 8, top: 8, bottom: 16 } : ARTICLE_POPOVER_PADDING;

	const getReferenceClientRect = useCallback(() => {
		const position = editor.storage?.comment?.openedComment?.position;
		if (!position) return { top: 0, left: 0, width: 0, height: 0 } as DOMRect;

		const block = isBlockAt(editor.state.doc, position.from)
			? (editor.view.nodeDOM(position.from) as HTMLElement)?.firstElementChild
			: null;
		return block?.getBoundingClientRect() ?? posToDOMRect(editor.view, position.from, position.to);
	}, [editor]);

	const openedPosition = editor.storage?.comment?.openedComment?.position;
	const isBlockComment = Boolean(data && openedPosition && isBlockAt(editor.state.doc, openedPosition.from));
	const { placement, fallbackPlacements } = commentPlacement(isBlockComment);

	const onOutsideClick = useCallback(
		(_, event) => {
			const target = event.target as HTMLElement;
			if (editor.view.dom.contains(target) || isInDropdown(event)) return;
			hideComment();
		},
		[editor, hideComment],
	);

	return (
		<div ref={elementRef} style={styles}>
			<Tooltip
				animation="article-popover"
				appendTo={() => (appendCommentToBody ? document.body : editor.view.dom.parentElement)}
				arrow={false}
				content={
					<GlobalEditorIsEditable.Provider value={editor?.isEditable}>
						{data && (
							<Comment
								commentId={dataCommentId}
								data={data}
								key={dataCommentId}
								onAddAnswer={onAddAnswer}
								onClose={close}
								onCreate={onCreate}
								onDelete={onDelete}
								onDeleteAnswer={onDeleteAnswer}
								user={author}
							/>
						)}
					</GlobalEditorIsEditable.Provider>
				}
				contentClassName="article-popover"
				customStyle
				distance={4}
				duration={[220, 200]}
				getReferenceClientRect={getReferenceClientRect}
				hideInMobile={false}
				interactive
				maxWidth="none"
				onClickOutside={onOutsideClick}
				onHidden={onHidden}
				onMount={(instance) => {
					instanceRef.current = instance;
				}}
				placement={placement}
				popperOptions={{
					modifiers: [
						{
							name: "preventOverflow",
							options: {
								padding: popoverPadding,
								boundary: popoverBoundary,
							},
						},
						{
							name: "flip",
							options: {
								fallbackPlacements,
								boundary: popoverBoundary,
							},
						},
					],
				}}
				reference={elementRef}
				sticky={true}
				visible={isOpen}
				zIndex={10}
			/>
		</div>
	);
});

export default CommentView;
